import type { ProductWrapper } from '@state/product'
import type { Shelf, TransformedFacing, TransformedLayer } from '@state/shelf/types'

/** Thickness of a shelf board in meters; drawn below the layer's bottom. */
export const BOARD_HEIGHT_M = 0.04

export type ImageOutline = NonNullable<ProductWrapper['imageOutline']>

/** Used for images without a stored outline: the whole (square) image. */
const UNCROPPED: ImageOutline = { x0: 0, y0: 0, x1: 1, y1: 1, aspect: 1 }

type FacingKind = 'image' | 'noImage' | 'noGtin'

export interface LaidOutFacing {
  facing: TransformedFacing
  product?: ProductWrapper
  kind: FacingKind
  isOutOfShelf: boolean
  /** Horizontal position and width, fractions of the shelf width, overflow already scaled away. */
  left: number
  width: number
  /** Height of the facing box (product plus out-of-shelf margin), fraction of the layer height. */
  boxHeight: number
  /** Size of the product image or placeholder pack; width as fraction of the shelf width. */
  contentWidth: number
  contentHeight: number
  outline?: ImageOutline
}

/**
 * Lays out one layer of the shelf-like product view. Only uses the facing width, the layer height
 * and the image outline, see spec 1.2 of the shelf visualization epic.
 *
 * @param layerAspect width / height of the layer's usable area (shelf width / layer height, both
 *   in meters)
 */
export function layoutLayer(
  facings: TransformedFacing[],
  productsByGtin: Map<string, ProductWrapper>,
  layerAspect: number
): LaidOutFacing[] {
  if (facings.length === 0) return []

  // Facings reaching beyond the shelf are scaled back linearly, keeping order and relative widths
  const minLeft = Math.min(0, ...facings.map(f => f.left))
  const maxRight = Math.max(1, ...facings.map(f => f.left + f.width))
  const extent = maxRight - minLeft

  const normalized = facings.map((facing) => {
    const product = facing.gtin ? productsByGtin.get(facing.gtin) : undefined
    const kind: FacingKind = !facing.gtin ? 'noGtin' : product?.imageUrl ? 'image' : 'noImage'
    const outline = kind === 'image' ? product?.imageOutline ?? UNCROPPED : undefined
    return {
      facing,
      product,
      kind,
      outline,
      left: (facing.left - minLeft) / extent,
      width: facing.width / extent,
    }
  })

  // Image fitted into 92% of the facing width × 90% of the layer height
  const fittedHeight = (width: number, outline: ImageOutline) =>
    Math.min(0.9, (width * 0.92 * layerAspect) / outline.aspect)

  const imageHeights = normalized
    .filter(f => f.outline)
    .map(f => ({ height: fittedHeight(f.width, f.outline!), width: f.width }))
  const median = widthWeightedMedian(imageHeights) ?? 0.55
  const cap = Math.min(0.9, median * 1.25)
  const packHeight = Math.min(median, 0.7)

  return normalized.map((f) => {
    let contentWidth: number
    let contentHeight: number
    if (f.outline) {
      contentHeight = Math.min(fittedHeight(f.width, f.outline), cap)
      contentWidth = (contentHeight * f.outline.aspect) / layerAspect
    } else {
      contentHeight = packHeight
      contentWidth = Math.min(f.width * 0.84, packHeight / layerAspect)
    }

    return {
      ...f,
      isOutOfShelf: f.facing.stockLevel === 'NO_STOCK',
      contentWidth,
      contentHeight,
      boxHeight: Math.min(1, contentHeight * 1.75),
    }
  })
}

/**
 * Median of the image heights, each weighted by its facing width: a product on several narrow
 * facings counts as much as the shelf width it takes up, not once per facing. With equal widths
 * this is the plain (upper) median.
 */
function widthWeightedMedian(images: { height: number; width: number }[]) {
  const sorted = [...images].sort((a, b) => a.height - b.height)
  const half = sorted.reduce((sum, image) => sum + image.width, 0) / 2
  let cumulative = 0
  return sorted.find(image => (cumulative += image.width) > half)?.height
}

export function productsByGtin(products: ProductWrapper[]) {
  return new Map(products.map(p => [p.gtin, p]))
}

/** Usable height of a layer (fraction of the shelf height): the board of the layer above takes its top. */
/**
 * Gives the top layer the room of a typical layer if the shelf height leaves too little. The height
 * can be too low: the deploy-db's often is, and the robot's sometimes. Without room the top layer's
 * products shrink to slivers, or stick out of the view if the layer is above the height.
 */
export function withRoomAboveTopLayer(shelf: Shelf): Shelf {
  const { layers } = shelf
  const topLayer = layers.at(-1)
  if (!topLayer) return shelf

  const gaps = layers.slice(1).map((layer, index) => layer.y - layers[index]!.y).sort((a, b) => a - b)
  const typicalGap = gaps[Math.floor(gaps.length / 2)] ?? topLayer.y / 4
  const needed = topLayer.y + typicalGap
  if (needed <= 1) return shelf

  return {
    ...shelf,
    height: shelf.height * needed,
    layers: layers.map(layer => ({
      ...layer,
      y: layer.y / needed,
      height: (layer === topLayer ? typicalGap : layer.height) / needed,
    })),
  }
}

export function usableLayerHeight(layer: TransformedLayer, boardHeight: number) {
  return Math.max(layer.height - boardHeight, layer.height / 2)
}
